import { Mail, MessageSquare, TriangleAlert, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError } from '../../api/envelope';
import type { Campaign, CampaignResendSegment } from '../../api/types';
import { identityLine } from '../../lib/channelMeta';
import { useComms } from './context';
import { type FollowUpGroup, followUpCaution, followUpSections, initialFollowUp, pickable } from './followUp';
import { resendSegmentSize } from './campaignTools';

/**
 * Follow up on a finished campaign — a side panel over its results, so the
 * figures the choice is made from stay in view behind it.
 *
 * Every group the server can resolve for this channel is listed with its own
 * count, sorted by what the operator is asking (who showed interest, who did
 * not open it, who it never reached, everyone), and a group this campaign
 * cannot answer says so rather than reading as zero.
 *
 * It creates a DRAFT and hands it back; it does not send. That is the whole
 * shape of the feature: the mail then goes out through the builder's ordinary
 * Send, which is where the quota check, the credit choice, the pre-flight
 * review and — for an agent who can only draft — the review step live. A
 * one-click resend would be the single path putting unreviewed mail in front of
 * customers, on copy whose author is no longer even allowed to edit it.
 *
 * The counts come off the campaign's own funnel rather than a second request:
 * the server resolves each segment with predicates written to produce exactly
 * these numbers (scripts/verify/campaignresend.js holds the two sides together).
 */
export function CampaignFollowUpPanel({
  campaign,
  picked,
  initialSegment,
  onClose,
  onCreated,
}: {
  campaign: Campaign;
  /** Rows ticked in the results table, if the operator came in that way. */
  picked: string[];
  /** The group a next step named ("Follow up with the 431 who opened but didn't click"). */
  initialSegment?: CampaignResendSegment;
  onClose: () => void;
  onCreated: (draft: Campaign) => void;
}) {
  const { source, inboxes, noteSendDenied } = useComms();
  const isSms = campaign.channel === 'sms';
  const sections = followUpSections(campaign);
  const [segment, setSegment] = useState<CampaignResendSegment>(() =>
    initialFollowUp(campaign, picked.length, initialSegment),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set only when the draft came back SHORT of the count offered here. The
  // panel then stops instead of navigating: being told 41 and landing in a
  // builder that says 38, seconds later, is how an operator stops trusting the
  // numbers on this screen.
  const [short, setShort] = useState<(Campaign & { skippedRecipients: number }) | null>(null);

  const size = segment === 'custom' ? picked.length : resendSegmentSize(campaign.stats, segment);
  const everyone = resendSegmentSize(campaign.stats, 'all');
  const caution = followUpCaution(campaign, segment);
  const inbox = inboxes.find((one) => one.id === campaign.inboxId) ?? null;
  const from = inbox ? identityLine(inbox) : null;

  // Escape closes, as the console's other panels do — but never mid-create, or
  // the operator loses the screen while a campaign is being written behind it.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const create = () => {
    setBusy(true);
    setError(null);
    source.api
      .resendCampaign(campaign.id, {
        segment,
        // The ticked rows go in the field that matches the channel — the server
        // validates one as addresses and the other as E.164 numbers.
        ...(segment === 'custom' ? (isSms ? { phones: picked } : { emails: picked }) : {}),
      })
      .then(
        (draft) => {
          if (draft.skippedRecipients > 0) {
            setBusy(false);
            setShort(draft);
            return;
          }
          onCreated(draft);
        },
        (e: unknown) => {
          setBusy(false);
          if (e instanceof ApiError && e.kind === 'forbidden') noteSendDenied();
          setError(e instanceof ApiError ? e.message : 'Something went wrong.');
        },
      );
  };

  const row = (group: FollowUpGroup) => {
    const on = segment === group.key;
    const can = pickable(group);
    const share = everyone > 0 ? Math.min(100, Math.round((group.count / everyone) * 100)) : 0;
    return (
      <button
        key={group.key}
        type="button"
        role="radio"
        aria-checked={on}
        className={`fup-group${on ? ' on' : ''}${can ? '' : ' off'}`}
        // Nobody in the group is not a choice worth offering — the server refuses
        // it — and greyed out with the count still on it says why.
        disabled={!can || busy}
        onClick={() => setSegment(group.key)}
      >
        <span className="fup-dot" aria-hidden />
        <span className="fup-group-text">
          <span className="fup-group-label">{group.label}</span>
          <span className="fup-group-hint">{group.unavailable ?? group.hint}</span>
          {can && group.key !== 'all' && (
            <span className="fup-bar" aria-hidden>
              <span style={{ width: `${share}%` }} />
            </span>
          )}
        </span>
        <span className="fup-count">{group.unavailable ? '—' : group.count.toLocaleString()}</span>
      </button>
    );
  };

  return (
    // Mousedown, not click: a drag that starts inside the panel and ends on the
    // backdrop is a text selection, not a dismissal.
    <div
      className="fup-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <aside className="fup" role="dialog" aria-modal="true" aria-labelledby="fup-title">
        <header className="fup-head">
          <div className="fup-head-text">
            <h2 id="fup-title">Follow up</h2>
            <span className="fup-sub">{campaign.name}</span>
          </div>
          <button type="button" className="cbtn cbtn-icon" aria-label="Close" disabled={busy} onClick={onClose}>
            <X size={15} />
          </button>
        </header>

        <div className="fup-body">
          {short ? (
            <div className="cmg-warnbox">
              <TriangleAlert size={15} />
              <span>
                {short.skippedRecipients.toLocaleString()} of the {size.toLocaleString()} could not be carried
                over:{' '}
                {isSms
                  ? 'those numbers are not in a form a carrier accepts.'
                  : 'dispatch holds those addresses without a valid domain, so nothing can mail them.'}{' '}
                The draft is ready for the remaining {(short.customRecipients?.length ?? 0).toLocaleString()}.
              </span>
            </div>
          ) : (
            <>
              <p className="fup-lead">
                Pick who it goes to. This writes a <b>new draft</b> with the same{' '}
                {isSms ? 'text' : 'subject and body'} — nothing is sent until you send that draft, where the{' '}
                {isSms ? 'wording and the pace' : 'wording, the review and the pace'} can all still change.
              </p>

              <div role="radiogroup" aria-labelledby="fup-title" className="fup-sections">
                {picked.length > 0 && (
                  <section className="fup-section">
                    <h3>Your pick</h3>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={segment === 'custom'}
                      className={`fup-group${segment === 'custom' ? ' on' : ''}`}
                      disabled={busy}
                      onClick={() => setSegment('custom')}
                    >
                      <span className="fup-dot" aria-hidden />
                      <span className="fup-group-text">
                        <span className="fup-group-label">The ones you ticked</span>
                        <span className="fup-group-hint">Exactly the rows selected on Recipients</span>
                      </span>
                      <span className="fup-count">{picked.length.toLocaleString()}</span>
                    </button>
                  </section>
                )}
                {sections.map((section) => (
                  <section key={section.id} className="fup-section">
                    <h3>{section.title}</h3>
                    {section.groups.map(row)}
                  </section>
                ))}
              </div>
            </>
          )}
        </div>

        <footer className="fup-foot">
          {short ? (
            <button type="button" className="cbtn cbtn-primary fup-go" onClick={() => onCreated(short)}>
              Open the draft
            </button>
          ) : (
            <>
              <div className="fup-what">
                {isSms ? <MessageSquare size={14} aria-hidden /> : <Mail size={14} aria-hidden />}
                <span>
                  {from ? `From ${from}` : 'From the same sender'}
                  {/* Opt-outs are screened at every send, a follow-up's included —
                      said here because a list ticked off an old table predates them. */}
                  {' · anyone who has opted out since is skipped'}
                </span>
              </div>
              {caution && (
                <div className="cmg-warnbox">
                  <TriangleAlert size={15} />
                  <span>{caution}</span>
                </div>
              )}
              {error && <div className="comms-error-note">{error}</div>}
              <div className="fup-actions">
                <button type="button" className="cbtn" disabled={busy} onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="cbtn cbtn-primary fup-go"
                  disabled={busy || size === 0}
                  onClick={create}
                >
                  {busy && <span className="btn-spin" />}
                  {busy ? 'Creating…' : `Create draft for ${size.toLocaleString()}`}
                </button>
              </div>
            </>
          )}
        </footer>
      </aside>
    </div>
  );
}
